/**
 * Compares two position numbers ("1", "1.2", "1.10.2") segment by segment,
 * so "1.10" sorts after "1.2" and "1" before "1.1". A plain string compare
 * gets both wrong.
 */
export function comparePositionNumbers(a, b) {
  const partsA = String(a ?? '').split('.');
  const partsB = String(b ?? '').split('.');
  const length = Math.max(partsA.length, partsB.length);

  for (let i = 0; i < length; i++) {
    if (i >= partsA.length) return -1;
    if (i >= partsB.length) return 1;

    const numberA = Number(partsA[i]);
    const numberB = Number(partsB[i]);
    if (Number.isNaN(numberA) || Number.isNaN(numberB)) {
      if (partsA[i] !== partsB[i]) return partsA[i] < partsB[i] ? -1 : 1;
    } else if (numberA !== numberB) {
      return numberA - numberB;
    }
  }

  return 0;
}

function hasPositionNumber(position) {
  return (
    position?.position !== undefined &&
    position?.position !== null &&
    String(position.position) !== ''
  );
}

/**
 * Returns the positions ordered by their position number.
 *
 * The position number is the document order of a position. Lists that come
 * from outside (the API, another document) are not guaranteed to be in that
 * order, so anything that displays or edits them orders them by number first.
 *
 * The sort is stable, and it only runs when every position carries a number:
 * positions just created in the editor have none yet and get numbered from
 * the array order, so an array with unnumbered positions is returned as is.
 */
export function sortByPositionNumber(positions) {
  if (!Array.isArray(positions) || positions.length < 2) return positions;
  if (!positions.every(hasPositionNumber)) return positions;

  return [...positions].sort((a, b) =>
    comparePositionNumbers(a.position, b.position),
  );
}
